import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Car, Plus, Trash2, Pencil, Save, X, ShieldCheck, ScrollText, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = [
  { key: "itp", label: "ITP", icon: ShieldCheck, color: "emerald" },
  { key: "asigurare", label: "Asigurare", icon: ScrollText, color: "blue" },
  { key: "rovinieta", label: "Rovinieta", icon: CalendarClock, color: "amber" },
];

function ymd(date) {
  const tz = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tz).toISOString().slice(0, 10);
}

// Returns days remaining until `iso` date. Negative = expired.
function daysLeft(iso) {
  if (!iso) return null;
  const today = new Date(ymd(new Date()) + "T00:00:00");
  const target = new Date(iso + "T00:00:00");
  if (isNaN(target.getTime())) return null;
  const ms = target.getTime() - today.getTime();
  return Math.round(ms / 86400000);
}

function StatusBadge({ days }) {
  if (days === null || days === undefined) return null;
  let cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
  let label = `${days} zile`;
  if (days < 0) {
    cls = "bg-red-50 text-red-800 border-red-300";
    label = `EXPIRAT (${Math.abs(days)} zile)`;
  } else if (days === 0) {
    cls = "bg-red-50 text-red-800 border-red-300";
    label = "Expira AZI";
  } else if (days <= 7) {
    cls = "bg-red-50 text-red-800 border-red-200";
    label = `${days} zile`;
  } else if (days <= 30) {
    cls = "bg-amber-50 text-amber-800 border-amber-200";
    label = `${days} zile`;
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${cls}`}
      data-testid="vehicle-days-left"
    >
      {label}
    </span>
  );
}

const emptyForm = () => ({
  id: null,
  vin: "",
  brand: "",
  model: "",
  plate: "",
  items: [],
});

function saveButtonLabel(saving, editingId, addLabel) {
  if (saving) return "Se salveaza...";
  if (editingId) return "Salveaza modificarile";
  return addLabel;
}

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Stable id helper — used for fresh form items.
  const newItemId = () => (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `it-${performance.now()}`
  );

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const handle = setInterval(tick, 5000);
    return () => clearInterval(handle);
  }, [load]);

  const startCreate = () => setForm(emptyForm());
  const startEdit = (v) => setForm({
    id: v.id,
    vin: v.vin || "",
    brand: v.brand || "",
    model: v.model || "",
    plate: v.plate || "",
    items: (v.items || []).map((it) => ({ ...it })),
  });

  const addItem = (type) => {
    const newItem = { id: newItemId(), type, expires_at: "", notes: "" };
    setForm({ ...form, items: [...form.items, newItem] });
  };
  const updateItem = (idx, patch) => {
    const newItems = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items: newItems });
  };
  const removeItem = (idx) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    // Basic validation
    if (!(form.vin.trim() || form.brand.trim() || form.model.trim() || form.plate.trim())) {
      toast.error("Completeaza cel putin un camp (Nr., VIN, Marca sau Model)");
      return;
    }
    for (const it of form.items) {
      if (!it.expires_at) {
        toast.error(`Adauga data expirarii pentru ${it.type.toUpperCase()}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        vin: form.vin.trim(),
        brand: form.brand.trim(),
        model: form.model.trim(),
        plate: form.plate.trim(),
        items: form.items.map((it) => ({
          id: it.id,
          type: it.type,
          expires_at: it.expires_at,
          notes: it.notes || "",
        })),
      };
      if (form.id) {
        await api.put(`/vehicles/${form.id}`, payload);
        toast.success("Vehicul actualizat");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicul adaugat");
      }
      setForm(emptyForm());
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const deleteVehicle = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/vehicles/${confirmDelete.id}`);
      toast.success("Vehicul sters");
      if (form.id === confirmDelete.id) setForm(emptyForm());
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la stergere");
    } finally {
      setConfirmDelete(null);
    }
  };

  // Quick aggregate stat: number of items expired or expiring in ≤30 days
  const urgentCount = useMemo(() => {
    let n = 0;
    for (const v of vehicles) for (const it of v.items || []) {
      const d = daysLeft(it.expires_at);
      if (d !== null && d <= 30) n += 1;
    }
    return n;
  }, [vehicles]);

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="vehicles-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Flota
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Vehicule
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Gestioneaza vehiculele firmei si urmareste expirarile pentru ITP, asigurare si rovinieta.
            Zilele ramase pana la expirare se calculeaza automat.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Expira in 30 zile
          </span>
          <span
            className={`font-display text-2xl font-black tracking-tighter ${urgentCount > 0 ? "text-red-600" : "text-black"}`}
            data-testid="vehicles-urgent-count"
          >
            {urgentCount}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 items-start">
        {/* Vehicle list */}
        <div className="border border-zinc-200 rounded-sm bg-white">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight text-black">
              Vehicule inregistrate
            </h2>
            <span className="font-mono text-[11px] text-zinc-500">
              {vehicles.length} {vehicles.length === 1 ? "vehicul" : "vehicule"}
            </span>
          </div>
          {loading ? (
            <div className="p-10 text-center text-zinc-500 font-mono text-sm">Se incarca...</div>
          ) : vehicles.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">
              <Car size={32} className="mx-auto mb-2 text-zinc-300" />
              <div className="font-mono text-sm">Niciun vehicul. Adauga primul folosind formularul din dreapta.</div>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {vehicles.map((v) => (
                <div key={v.id} className="p-4" data-testid={`vehicle-row-${v.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-lg font-bold tracking-tight text-black" data-testid={`vehicle-plate-${v.id}`}>
                          {v.plate || "—"}
                        </span>
                        <span className="font-mono text-sm text-zinc-700">
                          {v.brand} {v.model}
                        </span>
                      </div>
                      {v.vin && (
                        <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                          VIN: {v.vin}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(v)}
                        className="h-8 w-8 hover:bg-blue-50"
                        data-testid={`edit-vehicle-${v.id}`}
                        title="Editeaza"
                      >
                        <Pencil size={14} className="text-blue-700" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(v)}
                        className="h-8 w-8 hover:bg-red-50"
                        data-testid={`delete-vehicle-${v.id}`}
                        title="Sterge"
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </div>

                  {(v.items || []).length > 0 ? (
                    <Table className="mt-3">
                      <TableHeader>
                        <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                          <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">Categorie</TableHead>
                          <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">Expira la</TableHead>
                          <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {v.items.map((it) => {
                          const type = ITEM_TYPES.find((t) => t.key === it.type);
                          const Icon = type?.icon || ShieldCheck;
                          const days = daysLeft(it.expires_at);
                          return (
                            <TableRow key={it.id} data-testid={`vehicle-item-${v.id}-${it.type}`}>
                              <TableCell className="text-black text-sm font-medium">
                                <div className="flex items-center gap-1.5">
                                  <Icon size={13} className="text-zinc-600" />
                                  {type?.label || it.type}
                                </div>
                              </TableCell>
                              <TableCell className="text-black font-mono text-sm">
                                {it.expires_at}
                              </TableCell>
                              <TableCell className="text-right">
                                <StatusBadge days={days} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="mt-2 text-xs text-zinc-500 font-mono italic">
                      Fara categorii inregistrate (ITP / asigurare / rovinieta).
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form (create / edit) */}
        <div className="border border-zinc-200 rounded-sm bg-white p-5 lg:sticky lg:top-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold tracking-tight text-black">
              {form.id ? "Editeaza vehicul" : "Adauga vehicul"}
            </h2>
            {form.id && (
              <button
                type="button"
                onClick={startCreate}
                className="font-mono uppercase text-[10px] tracking-wider text-zinc-500 hover:text-black flex items-center gap-1"
                data-testid="vehicle-form-cancel"
              >
                <X size={11} /> Anuleaza edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Nr. inmatriculare
              </Label>
              <Input
                value={form.plate}
                onChange={(e) => setForm({ ...form, plate: e.target.value })}
                placeholder="SV 12 COS"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black font-mono uppercase"
                data-testid="vehicle-plate-input"
              />
            </div>
            <div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Marca
              </Label>
              <Input
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                placeholder="VW"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="vehicle-brand-input"
              />
            </div>
            <div>
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Model
              </Label>
              <Input
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="Caddy"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="vehicle-model-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Serie sasiu (VIN)
              </Label>
              <Input
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value })}
                placeholder="WVWZZZ1KZBW123456"
                className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black font-mono"
                data-testid="vehicle-vin-input"
              />
            </div>
          </div>

          {/* Items: ITP / Asigurare / Rovinieta */}
          <div className="mt-5 pt-5 border-t border-dashed border-zinc-200">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Categorii (data expirarii)
              </Label>
            </div>
            <div className="space-y-2">
              {form.items.length === 0 ? (
                <div className="text-[11px] text-zinc-500 font-mono italic">
                  Adauga ITP, asigurare sau rovinieta cu data expirarii folosind butoanele de mai jos.
                </div>
              ) : (
                form.items.map((it, idx) => {
                  const type = ITEM_TYPES.find((t) => t.key === it.type);
                  const Icon = type?.icon || ShieldCheck;
                  const days = daysLeft(it.expires_at);
                  return (
                    <div
                      key={it.id || `idx-${idx}`}
                      className="border border-zinc-200 rounded-sm p-2.5 bg-zinc-50/40"
                      data-testid={`vehicle-form-item-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={13} className="text-zinc-700" />
                        <span className="font-mono uppercase text-[11px] tracking-wider text-black font-semibold flex-1">
                          {type?.label || it.type}
                        </span>
                        {days !== null && it.expires_at && <StatusBadge days={days} />}
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`vehicle-form-item-remove-${idx}`}
                          title="Sterge"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <Input
                        type="date"
                        value={it.expires_at || ""}
                        onChange={(e) => updateItem(idx, { expires_at: e.target.value })}
                        className="h-9 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
                        data-testid={`vehicle-form-item-date-${idx}`}
                      />
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {ITEM_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => addItem(t.key)}
                  className="px-2.5 py-1 rounded-sm border border-zinc-300 bg-white text-black hover:border-zinc-500 font-mono uppercase text-[10px] tracking-wider flex items-center gap-1.5"
                  data-testid={`vehicle-form-add-${t.key}`}
                >
                  <Plus size={11} /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            className="w-full mt-5 rounded-sm bg-black hover:bg-zinc-800 text-white h-11"
            data-testid="vehicle-save"
          >
            <Save size={16} className="mr-2" />
            {saveButtonLabel(saving, form.id, "Adauga vehicul")}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sterge vehiculul?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.plate} {confirmDelete?.brand} {confirmDelete?.model}. Aceasta actiune nu poate fi anulata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuleaza</AlertDialogCancel>
            <AlertDialogAction onClick={deleteVehicle} className="bg-red-600 hover:bg-red-700">
              Sterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
